import { useState } from 'react';
import { useGameStore, TOKEN_CAP } from '@/state/gameStore';
import { useDialogueStore } from '@/state/dialogueStore';
import {
  selectBezelState,
  selectCowardStatus,
  selectCurrentLevel,
  selectFaceState,
  selectSpiteMode,
  selectTokens,
} from '@/state/selectors';
import {
  FACE_STATES,
  FLAGS,
  MAX_LEVEL_NUMBER,
  type LevelId,
  type LevelNumber,
} from '@/state/types';
import { isDevPanelEnabled } from './urlParams';
import styles from './DevTools.module.css';

export function DevTools() {
  if (!isDevPanelEnabled(window.location.search)) return null;
  return <DevToolsPanel />;
}

function DevToolsPanel() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={styles.panel}
      data-testid="devtools-panel"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <header className={styles.header}>
        <span className={styles.title}>DEVTOOLS</span>
        <button
          type="button"
          className={styles.collapse}
          data-testid="devtools-collapse"
          onClick={() => {
            setCollapsed((c) => !c);
          }}
        >
          {collapsed ? '+' : '-'}
        </button>
      </header>
      {collapsed ? null : (
        <div className={styles.body}>
          <LevelSection />
          <FaceSection />
          <TokenSection />
          <FlagsSection />
          <DangerSection />
        </div>
      )}
    </aside>
  );
}

/* ------------------------------------------------------------------ */

const STAGING_LEVELS: readonly { n: number; label: string }[] = [
  { n: 33, label: 'LA — Any Key' },
  { n: 34, label: 'LI — High-Gloss Squeak (L41)' },
  { n: 36, label: 'LK — Meaning of Life (L44)' },
  { n: 38, label: 'LF — Empathy Calibration' },
  { n: 40, label: 'LH — Inverse Carousel' },
];

const LEVEL_OPTIONS: readonly { value: string; label: string; id: LevelId }[] = [
  ...Array.from({ length: MAX_LEVEL_NUMBER + 1 }, (_, n) => ({
    value: `LEVEL:${String(n)}`,
    label: `L${String(n).padStart(2, '0')}`,
    id: { kind: 'LEVEL', n: n as LevelNumber } as LevelId,
  })),
  { value: 'FALSE_ENDING', label: 'False Ending', id: { kind: 'FALSE_ENDING' } },
  { value: 'ENDGAME', label: 'Endgame', id: { kind: 'ENDGAME' } },
  ...STAGING_LEVELS.map(({ n, label }) => ({
    value: `LEVEL:${String(n)}`,
    label,
    id: { kind: 'LEVEL', n: n as LevelNumber } as LevelId,
  })),
];

function levelKey(id: LevelId): string {
  if (id.kind === 'LEVEL') return `LEVEL:${String(id.n)}`;
  return id.kind;
}

function LevelSection() {
  const current = useGameStore(selectCurrentLevel);
  const value = levelKey(current);
  return (
    <section className={styles.section} data-testid="devtools-levels">
      <label className={styles.label}>
        Level
        <select
          className={styles.select}
          data-testid="devtools-level-select"
          value={value}
          onChange={(e) => {
            const option = LEVEL_OPTIONS.find((o) => o.value === e.target.value);
            if (option) useGameStore.getState().advanceTo(option.id);
          }}
        >
          {LEVEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function FaceSection() {
  const face = useGameStore(selectFaceState);
  return (
    <section className={styles.section} data-testid="devtools-face">
      <span className={styles.label}>Face · {face}</span>
      <div className={styles.row}>
        {FACE_STATES.map((f) => (
          <button
            key={f}
            type="button"
            className={styles.chip}
            data-testid={`devtools-face-${f}`}
            data-active={f === face ? 'true' : 'false'}
            onClick={() => {
              useGameStore.getState().setFaceState(f);
            }}
          >
            {f}
          </button>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function TokenSection() {
  const tokens = useGameStore(selectTokens);
  return (
    <section className={styles.section} data-testid="devtools-tokens">
      <label className={styles.label}>
        Tokens · {String(tokens)} / {String(TOKEN_CAP)}
        <input
          type="range"
          min={0}
          max={TOKEN_CAP}
          step={1}
          value={tokens}
          data-testid="devtools-tokens-range"
          onChange={(e) => {
            const next = Number.parseInt(e.target.value, 10);
            if (Number.isInteger(next)) useGameStore.setState({ tokens: next });
          }}
        />
      </label>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function FlagsSection() {
  const flags = useGameStore((s) => s.flags);
  return (
    <section className={styles.section} data-testid="devtools-flags">
      <span className={styles.label}>Flags</span>
      <div className={styles.col}>
        {FLAGS.map((f) => (
          <label key={f} className={styles.check}>
            <input
              type="checkbox"
              data-testid={`devtools-flag-${f}`}
              checked={flags[f]}
              onChange={(e) => {
                useGameStore.getState().setFlag(f, e.target.checked);
              }}
            />
            <span>{f}</span>
          </label>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function DangerSection() {
  const coward = useGameStore(selectCowardStatus);
  const spite = useGameStore(selectSpiteMode);
  const bezel = useGameStore(selectBezelState);
  return (
    <section className={styles.section} data-testid="devtools-danger">
      <span className={styles.label}>
        Flags · coward {coward ? 'Y' : 'N'} · spite {spite ? 'Y' : 'N'} · dents{' '}
        {String(bezel.dents)}
      </span>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.danger}
          data-testid="devtools-reset"
          onClick={() => {
            useGameStore.getState()._devReset();
            useDialogueStore.getState()._devReset();
          }}
        >
          Clear save
        </button>
      </div>
    </section>
  );
}
